import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Popover,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Copy,
  Gear,
  Info,
  TextAlignLeft,
  UploadSimple,
  XCircle,
} from '@phosphor-icons/react';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { Position, useEdges, useReactFlow } from 'reactflow';
import { UploadService } from '../../service/UploadService';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { useNotificationStore } from '../../store/storeNotifications';
import { useSocketStore } from '../../store/storeSocket';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { isActiveEdge } from '../../util/findActiveHandle';
import { NodeProps } from '../../util/Types/NodeProps';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import TagInput from '../Test/TagInput';
import InputHandle from '../UI/InputHandle/InputHandle';
import Label from '../UI/Label/Label';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import PreviewModal from '../UI/PreviewModal/PreviewModal';

const VisuallyHiddenInput = styled('input')({
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: 1,
});

function ApiInput({ data, id, type }: NodeProps) {
  const { user } = useAuthStore(state => state);
  const edges = useEdges();
  const setNotification = useNotificationStore(state => state.setNotification);
  const { setNodes: setNodeState } = useReactFlow();
  const { setSchema, setSaving, flowId } = useFlowsStore(state => state);
  const { socket } = useSocketStore(state => state);

  const [dataValue, setDataValue] = useState(data?.text);
  const [linkValue, setLinkValue] = useState('');

  const [uploadedFiles, setUploadedFiles] = useState<any>(
    data.uploadedFiles ? data.uploadedFiles : [],
  );
  const [extractText, setExtractText] = useState(data?.extractText);
  const [isExtraction, setIsExtraction] = useState(
    data?.isExtraction && data.isExtraction,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isOpenPreviewModal, setIsOpenPreviewModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isChangingDirectly, setIsChangingDirectly] = useState(false);
  const [uploadedFilesCollapsed, setUploadedFilesCollapsed] = useState(true);
  const [isOpenCollapsed, setIsOpenCollapsed] = useState(true);

  async function handleUploadFile(event: any) {
    const file = event.target.files[0];

    if (file) {
      try {
        setNotification({
          type: 'info',
          message: 'Extracting the PDF',
        });
        setIsLoading(true);

        const formData = new FormData();
        formData.append('file', file);

        await UploadService.extractText(socket?.id!, formData);
      } catch (error: any) {
        setIsLoading(false);
        setNotification({
          type: 'error',
          message: error.message || 'Something went wrong',
        });
      }
    }
  }
  
  async function handleUploadUrlFile() {
    setIsLoading(true);
    if (linkValue) {
      if (isExtraction) {
        try {
          setNotification({
            type: 'info',
            message: 'Extracting the link...',
          });
          setIsLoading(true);

          const respose: any = await UploadService.extractTextByLink({
            url: linkValue,
          });

          const file = respose.data[0];

          setExtractText((prev: any) => {
            if (prev) {
              return `${prev}\n\nFile: ${linkValue}\n${file.trim()}`;
            } else {
              return `File: ${linkValue}\n${file.trim()}`;
            }
          });

          setUploadedFiles([
            ...uploadedFiles,
            { name: linkValue, url: linkValue },
          ]);
          setIsChangingDirectly(true);
          setUploadedFilesCollapsed(true);
          setIsLoading(false);
          setLinkValue('');

          setNotification({
            type: 'success',
            message: 'File extracted successfully',
          });
        } catch (error: any) {
          setIsLoading(false);

          setNotification({
            type: 'error',
            message: error.message || 'Something went wrong',
          });
        }
      } else {
        setNotification({
          type: 'info',
          message: 'Sending link without extracting',
        });

        setExtractText((prev: any) => {
          if (prev) {
            return `${prev}\n\nUrl: ${linkValue}`;
          } else {
            return `Url: ${linkValue}`;
          }
        });

        setUploadedFiles([
          ...uploadedFiles,
          { name: linkValue, url: linkValue },
        ]);
        setIsChangingDirectly(true);
        setLinkValue('');

        setNotification({
          type: 'success',
          message: 'Link sent',
        });
      }
    }

    setIsLoading(false);
  }
  function handleDeleteUpload(fileName: string) {
    const filteredItems = uploadedFiles.filter(
      (item: any) => item.name !== fileName,
    );

    const regex = new RegExp(
      `(?:File: ${fileName}|Url: ${fileName})([\\s\\S]*?)(?=\\n\\n|$)`,
      'g',
    );
    const updatedText = extractText.replace(regex, '').trim();

    setUploadedFiles(filteredItems);
    setExtractText(updatedText);
    setIsChangingDirectly(true);

    setNotification({
      type: 'info',
      message: 'Item deleted successfully',
    });
  }

  useEffect(() => {
    const handleExtractTextResponse = async (res: any) => {
      if (res.data) {
        const { file, fileName, socketId, url } = res.data;

        setExtractText((prev: any) => {
          if (prev) {
            return `${prev}\n\nFile: ${fileName}\n${file.trim()}`;
          } else {
            return `File: ${fileName}\n${file.trim()}`;
          }
        });

        setUploadedFiles([...uploadedFiles, { name: fileName, url }]);
        setIsChangingDirectly(true);
        setUploadedFilesCollapsed(true);
        setIsLoading(false);

        setNotification({
          type: 'success',
          message: 'File extracted successfully',
        });
      }
    };

    if (socket?.id) {
      socket?.on('extract-text', handleExtractTextResponse);
    }

    return () => {
      socket?.off('extract-text', handleExtractTextResponse);
    };
  }, [socket?.id, uploadedFiles, data]);

  useDebounceEffect(
    () => {
      if (isChangingDirectly) {
        setIsChangingDirectly(false);

        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          flowId,
          changeType: 'changeText',
          objectData: {
            text: dataValue,
            extractText,
            uploadedFiles,
            isExtraction,
          },
          setSchema,
        });
      }
    },
    [isChangingDirectly, dataValue, extractText, uploadedFiles],
    500,
  );

  const handleInputFileChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      setLinkValue(evt.target.value);
    },
    [],
  );
  const handleInputDataChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      setDataValue(evt.target.value);
      setIsChangingDirectly(true);
    },
    [],
  );
  function handleLinkOrExtrationOption(event: any) {
    if (event.target.value === 'extraction') {
      setIsExtraction(true);
    } else {
      setIsExtraction(false);
    }
  }

  function handleOpenPopoverSettings(event: {
    currentTarget: React.SetStateAction<HTMLButtonElement | null> | any;
  }) {
    setAnchorEl(event.currentTarget);
  }
  function handleClosePopoverSettings() {
    setAnchorEl(null);
  }

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;

  return (
    <Node
      id={id}
      data={data}
      isLoading={false}
      type={type}
      showTokensUsed={false}
    >
      <Box p="16px" position={'relative'}>
        <TagInput
          name={'text'}
          value={dataValue}
          isCollapsed={true}
          findColor={findColor}
          labelName={'Data'}
          nodeLabel={data?.label}
          nodeId={id}
          onChange={handleInputDataChange}
          infoText="Insert a data"
        />

        <Box
          display="flex"
          justifyContent="space-between"
          gap="4px"
          alignItems="center"
        >
          <TagInput
            name={'extractText'}
            value={linkValue}
            findColor={findColor}
            labelName={'File'}
            isCollapsed={true}
            nodeLabel={data?.label}
            nodeId={id}
            onChange={handleInputFileChange}
            onSettings={handleOpenPopoverSettings}
            placeholder="Link goes here"
            infoText="Enter a link or select an upload"
            hasSettings={true}
            width="324px"
            onIsOpenCollapsed={setIsOpenCollapsed}
          />

          {isOpenCollapsed &&
            (linkValue !== '' && linkValue ? (
              <Button
                onClick={handleUploadUrlFile}
                startIcon={
                  <UploadSimple size={18} style={{ color: '#FD746C' }} />
                }
                sx={{
                  padding: '6px',
                  backgroundColor: '#F9FAFB',
                  boxShadow: 'none',
                  border: '1px solid #FD746C',
                  minWidth: 0,
                  height: '100%',
                  marginTop: '15px',
                  '& .MuiButton-startIcon': {
                    margin: 0,
                  },
                  '&:hover': {
                    backgroundColor: '#F9FAFB',
                    border: '1px solid #FD746C',
                    boxShadow: 'none',
                  },
                }}
              ></Button>
            ) : (
              <Button
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                disabled={isLoading}
                startIcon={
                  isLoading ? (
                    <CircularProgress size={16} sx={{ color: '#FD746C' }} />
                  ) : (
                    <UploadSimple size={18} style={{ color: '#FD746C' }} />
                  )
                }
                sx={{
                  padding: '6px',
                  height: '100%',
                  backgroundColor: '#F9FAFB',
                  boxShadow: 'none',
                  border: '1px solid #FD746C',
                  minWidth: 0,
                  marginTop: '16px',
                  '& .MuiButton-startIcon': {
                    margin: 0,
                  },
                  '&:hover': {
                    backgroundColor: '#F9FAFB',
                    border: '1px solid #FD746C',
                    boxShadow: 'none',
                  },
                }}
              >
                <VisuallyHiddenInput type="file" onChange={handleUploadFile} />
              </Button>
            ))}
        </Box>

        <Box display="flex" gap="8px">
          <Label
            labelName="Uploaded files"
            collapsed={uploadedFilesCollapsed}
            onCollapse={() =>
              setUploadedFilesCollapsed(!uploadedFilesCollapsed)
            }
          />

          <Tooltip title="Files and links sents" placement="top" arrow>
            <Info size={16} color="#101828" />
          </Tooltip>

          <Tooltip title="Preview" placement="top" arrow>
            <TextAlignLeft
              size={16}
              color={'#667085'}
              onClick={() => setIsOpenPreviewModal(true)}
            />
          </Tooltip>
        </Box>

        <Collapse in={uploadedFilesCollapsed}>
          <Box display="flex" flexDirection="column" gap="3px">
            {uploadedFiles !== undefined &&
              uploadedFiles.map((file: any) => (
                <Chip
                  key={file.name}
                  sx={{
                    width: '412px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderRadius: '4px',
                  }}
                  label={file.name}
                  onDelete={() => handleDeleteUpload(file.name)}
                />
              ))}
          </Box>
        </Collapse>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClosePopoverSettings}
          PaperProps={{
            sx: {
              backgroundColor: user?.theme === 'dark' ? '#101828' : '#FFFFFF',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              padding: '16px',
            },
          }}
        >
          <Box display="flex" width="100%" justifyContent="end">
            <XCircle
              size={24}
              onClick={handleClosePopoverSettings}
              style={{ cursor: 'pointer' }}
            />
          </Box>

          <Box display="flex" flexDirection="column" gap="24px">
            <FormControl
              sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
            >
              <Box display="flex" gap="8px" alignItems="center">
                <Gear color="#101828" size={24} />
                <FormLabel sx={{ color: '#101828' }}>
                  How do you want to process the files?
                </FormLabel>
              </Box>

              <RadioGroup
                aria-labelledby="demo-radio-buttons-group-label"
                defaultValue={isExtraction ? 'extraction' : 'url'}
                name="radio-buttons-group"
                onChange={handleLinkOrExtrationOption}
                sx={{ gap: '24px' }}
              >
                <FormControlLabel
                  value="extraction"
                  control={<Radio />}
                  label={
                    <>
                      <Typography
                        sx={{
                          color: '#475467',
                          fontWeight: '600',
                          fontSize: '14px',
                        }}
                      >
                        Convert files to text
                      </Typography>
                      <Typography
                        sx={{
                          color: '#475467',
                          fontWeight: '400',
                          fontSize: '14px',
                        }}
                      >
                        (Extracts and processes the text from the file)
                      </Typography>
                    </>
                  }
                />

                <FormControlLabel
                  value="url"
                  control={<Radio />}
                  label={
                    <>
                      <Typography
                        sx={{
                          color: '#475467',
                          fontWeight: '600',
                          fontSize: '14px',
                        }}
                      >
                        Pass file URL
                      </Typography>
                      <Typography
                        sx={{
                          color: '#475467',
                          fontWeight: '400',
                          fontSize: '14px',
                        }}
                      >
                        (Sends the file URL without converting the contents)
                      </Typography>
                    </>
                  }
                />
              </RadioGroup>
            </FormControl>

            <Button
              onClick={() => {
                setIsChangingDirectly(true);
                handleClosePopoverSettings();
              }}
              variant="contained"
            >
              Save
            </Button>
          </Box>
        </Popover>

        <PreviewModal
          data={{
            value: data.extractText,
            readOnly: true,
            labelName: 'Uploaded Files',
            nodeLabel: 'Extract Text',
          }}
          name={'extract-text'}
          onHide={() => {
            setIsOpenPreviewModal(!isOpenPreviewModal);
          }}
          show={isOpenPreviewModal}
          type="Output"
        />

        <InputHandle
          activeColor={findColor?.color}
          handleId={'output'}
          isActive={isActiveEdge(edges, id, 'output', 'source')}
          right={-10}
          top={25}
          position={Position.Right}
          type="source"
        />

        <InputHandle
          activeColor={findColor?.color}
          handleId={'outputExtractText'}
          isActive={isActiveEdge(edges, id, 'outputExtractText', 'source')}
          right={-10}
          top={165}
          position={Position.Right}
          type="source"
        />

        <Divider sx={{ mt: '16px' }} />

        <Box
          onClick={() => {
            navigator.clipboard.writeText(data.myId);
            setNotification({
              message: 'Copied to clipboard',
              type: 'success',
            });
          }}
          sx={{
            borderRadius: '4px',
            padding: '6px 8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
            width: 'fit-content',
            mt: '24px',
            '&:hover': {
              background: user?.theme === 'dark' ? '#101828' : '#E8E8E8',
            },
          }}
          className="d-flex align-items-center gap-2"
        >
          <Typography
            sx={{
              fontSize: '12px',
              color: user?.theme === 'dark' ? '#6E7A8A' : '#D0D5DD',
              lineHeight: '16px',
              fontWeight: 400,
            }}
          >
            ID: {id}
          </Typography>

          <Copy
            color={user?.theme === 'dark' ? '#6E7A8A' : '#D0D5DD'}
            cursor={'pointer'}
            className="nodrag"
          />
        </Box>
      </Box>
    </Node>
  );
}

export default memo(ApiInput);
